import React, {useEffect, useState} from 'react';
import {canInstall, installStaffApp, isStaffOrigin, registerStaffPwa, pushRequest, disableStaffPush} from '@/lib/staffPwa';

export function StaffApp() {
  const [installable, setInstallable] = useState(canInstall());
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const apple = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  useEffect(() => {
    const update = () => setInstallable(canInstall());
    window.addEventListener('pout-install-ready', update);
    if (isStaffOrigin()) pushRequest('config').then(setSettings).catch(error => setMessage(error.message));
    if (isStaffOrigin() && supported) registerStaffPwa().then(async registration => {
      const sub = await registration.pushManager.getSubscription();
      if (sub) setEnabled((await pushRequest('status', {endpoint: sub.endpoint})).enabled);
    }).catch(error => setMessage(error.message));
    return () => window.removeEventListener('pout-install-ready', update);
  }, [supported]);
  const action = async (fn) => {
    setBusy(true); setMessage('');
    try { await fn(); } catch (error) { setMessage(error.message || 'Please try again.'); } finally { setBusy(false); }
  };
  const enable = () => action(async () => {
    // Permission must be requested directly from the user's button press on iOS.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Notifications are blocked. Allow notifications for Pout Staff in your device or browser settings, then try again.');
    const registration = await registerStaffPwa();
    if (!registration.active) await navigator.serviceWorker.ready;
    const key = settings.public_key.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(key + '='.repeat((4 - key.length % 4) % 4)), c => c.charCodeAt(0));
    const subscription = await registration.pushManager.getSubscription() || await registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: bytes});
    await pushRequest('subscribe', subscription.toJSON());
    setEnabled(true); setMessage('Notifications enabled on this device. Use Send test notification to check delivery.');
  });
  const button = 'rounded-full px-5 py-3 bg-[#c3a052] text-[#101A3A] font-semibold disabled:opacity-50';
  return <div className="p-5 md:p-8 max-w-3xl space-y-6">
    <h1 className="text-3xl font-serif">App &amp; notifications</h1>
    {!isStaffOrigin() ? <p>Open <a className="underline" href="https://staff.poutaesthetics.co.uk/staff/settings/app">the permanent staff portal</a> to install the app and enable notifications.</p> : <>
      <section className="rounded-2xl bg-[#101A3A] text-white p-6 space-y-4">
        <h2 className="text-2xl font-serif text-[#c3a052]">Pout Staff on your home screen</h2>
        {standalone ? <p>You’re using the installed app.</p> : <>
          {installable && <button className={button} disabled={busy} onClick={() => action(async () => {await installStaffApp(); setInstallable(canInstall());})}>Install Pout Staff</button>}
          <p>{apple ? 'In Safari, tap Share, then Add to Home Screen. Open Pout Staff from its new icon before enabling notifications.' : 'Use Install Pout Staff above, or open your browser menu and choose Install app or Add to Home screen.'}</p>
        </>}
      </section>
      <section className="rounded-2xl border border-[#c3a052] bg-white p-6 space-y-4">
        <h2 className="text-2xl font-serif">Staff notifications</h2>
        <p>Receive new bookings, cancellations and completed forms. Customer details stay inside the signed-in portal.</p>
        <p>Enable this on each device you want to receive alerts. Signing out turns off alerts on that device.</p>
        {!supported || (apple && !standalone) ? <p>Install and open the app first. Notifications need a supported browser and, on iPhone or iPad, iOS/iPadOS 16.4 or later.</p> : !settings ? <p>Checking notification settings…</p> : !settings.enabled ? <p>Notification setup is not complete on the server yet.</p> : <>
          <p>{enabled ? 'Enabled on this device' : 'Not enabled on this device'}</p>
          <div className="flex flex-wrap gap-3">
            {enabled ? <>
              <button className={button} disabled={busy} onClick={() => action(async () => {
                const sub = await (await registerStaffPwa()).pushManager.getSubscription();
                if (!sub) throw new Error('Enable notifications again on this device.');
                const result = await pushRequest('test', {endpoint: sub.endpoint}); setMessage(result.message);
              })}>Send test notification</button>
              <button className="rounded-full border px-5 py-3" disabled={busy} onClick={() => action(async () => {await disableStaffPush(); setEnabled(false); setMessage('Notifications turned off on this device.');})}>Turn off notifications</button>
            </> : <button className={button} disabled={busy} onClick={enable}>Enable notifications</button>}
          </div>
        </>}
      </section>
    </>}
    {message && <p role="status" className="rounded-xl border p-4">{message}</p>}
  </div>;
}
